import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { isBefore, parseISO } from 'date-fns';
import './CheckIn.css';

const CheckIn: React.FC = () => {
  const { dia } = useParams<{ dia: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState<'loading' | 'valid' | 'invalid' | 'expired' | 'success'>('loading');
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const verifyToken = async () => {
      if (!auth.currentUser) {
        navigate('/acesso?redirect=' + window.location.pathname + window.location.search);
        return;
      }

      try {
        const configSnap = await getDoc(doc(db, 'config_evento', 'tokens'));
        if (configSnap.exists()) {
          const tokenData = configSnap.data()[dia || ''];
          
          if (!tokenData || tokenData.token !== token) {
            setStatus('invalid');
            setMessage('Token de presença inválido.');
            return;
          }

          if (isBefore(parseISO(tokenData.expiresAt), new Date())) {
            setStatus('expired');
            setMessage('O link de presença para hoje expirou.');
            return;
          }

          // --- NUEVA VERIFICACIÓN: ¿Ya marcó presencia? ---
          const userRef = doc(db, 'participantes', auth.currentUser.uid);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists() && userSnap.data().asistencia?.[dia || '']) {
            setStatus('success');
            setMessage('Sua presença já foi confirmada anteriormente para este dia.');
            return;
          }

          setStatus('valid');
        } else {
          setStatus('invalid');
          setMessage('Configuração de evento não encontrada.');
        }
      } catch (err) {
        setStatus('invalid');
        setMessage('Erro ao verificar token.');
      }
    };

    verifyToken();
  }, [dia, token, navigate]);

  const handleMarkPresence = async () => {
    if (!auth.currentUser || !dia) return;

    try {
      const userRef = doc(db, 'participantes', auth.currentUser.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const currentAsistencia = userSnap.data().asistencia;
        await updateDoc(userRef, {
          [`asistencia.${dia}`]: true
        });
        setStatus('success');
        setMessage('Presença confirmada com sucesso para ' + dia + '!');
      } else {
        setMessage('Usuário não encontrado na lista de participantes.');
      }
    } catch (err) {
      setMessage('Erro ao marcar presença. Tente novamente.');
    }
  };

  return (
    <div className="checkin-page container">
      <div className="checkin-card">
        {status === 'loading' && <p>Verificando link de presença...</p>}
        
        {(status === 'invalid' || status === 'expired') && (
          <div className="status-error">
            <h2>Ops!</h2>
            <p>{message}</p>
            <button onClick={() => navigate('/')} className="btn btn-secondary">Voltar ao Início</button>
          </div>
        )}

        {status === 'valid' && (
          <div className="status-valid">
            <h2>Confirmar Presença</h2>
            <p>Você está acessando o link do <strong>{dia}</strong>.</p>
            <button onClick={handleMarkPresence} className="btn btn-primary btn-large">
              Marcar Minha Presença Agora
            </button>
          </div>
        )}

        {status === 'success' && (
          <div className="status-success">
            <div className="success-icon">✓</div>
            <h2>Tudo certo!</h2>
            <p>{message}</p>
            <button onClick={() => navigate('/')} className="btn btn-primary">Ir para o Site</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckIn;
